"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import { memo, useEffect } from "react";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "components/Button";
import Modal from "components/Modal";
import RequiredBadge from "components/RequiredBadge";
import Textarea from "components/Textarea";
import { TEST_IDS } from "constants/testIds";
import { VALIDATION_MESSAGE } from "constants/validation";

export type MemoInput = {
  memo: string;
};

export const schema = yup.object().shape({
  memo: yup.string().optional().max(1000, VALIDATION_MESSAGE.TOO_LARGE_NUMBER(1000)),
});

const MemoModal = memo(
  ({
    isOpen,
    onConfirm,
    onClose,
    memo,
  }: {
    isOpen: boolean;
    onConfirm: (data: MemoInput) => Promise<void>;
    onClose: () => void;
    memo: string;
  }) => {
    const { register, formState, handleSubmit, reset } = useForm<MemoInput>({
      mode: "onBlur",
      resolver: yupResolver(schema) as Resolver<MemoInput>,
      defaultValues: {
        memo,
      },
    });

    const handleClick = handleSubmit(async (data) => {
      const trimmedMemo = data.memo.trim();

      await onConfirm({
        memo: trimmedMemo,
      });

      reset({ memo: trimmedMemo });
    });

    const handleClose = () => {
      reset({ memo });

      onClose();
    };

    useEffect(() => {
      reset({ memo });
    }, [memo, reset]);

    return (
      <Modal isOpen={isOpen} onClose={handleClose}>
        <div className="flex w-[800px] flex-col rounded-lg bg-white px-8 pb-5 pt-4" data-testid={TEST_IDS.MEMO_MODAL.SIGN_MODAL}>
          <div className="flex">
            <div className="flex h-fit min-h-[48px] w-[140px] items-center gap-x-2">
              <RequiredBadge isActive={false} />
              <span>メモ</span>
            </div>
            <div className="flex flex-1 flex-col">
              <div className="pb-1 text-s text-gray">
                ※管理会社使用欄のため、募集サイトや賃貸借契約書には表示されません
              </div>
              <Textarea
                required={false}
                register={register}
                name="memo"
                height="h-[100px]"
                placeholder="連絡事項などを記入ください"
              />
              {formState.errors.memo && (
                <span
                  className="relative top-2 -my-2 text-xs font-bold text-attention"
                  role="alert"
                  aria-label={formState.errors.memo?.message}
                >
                  {formState.errors.memo?.message}
                </span>
              )}
            </div>
          </div>
          <div className="-mx-8 mt-4 flex justify-center gap-4 border-t border-border pt-5">
            <Button type="button" onClick={handleClose} style="outline" width="w-[160px]" text="キャンセル" />
            <Button type="button" onClick={handleClick} style="primary" width="w-[160px]" text="保存する" />
          </div>
        </div>
      </Modal>
    );
  }
);

MemoModal.displayName = "MemoModal";

export default MemoModal;
